import { useMemo, useState } from "react";
import type { Capability, CapabilitiesResponse } from "./capabilityTypes";
import {
  DEFAULT_FILTER_STATE,
  PAGE_SIZE,
  PRESET_VIEWS,
  type FilterState,
  type SortField,
  applyFilters,
  fieldValue,
  paginate,
  parseFilterState,
  serializeFilterState,
} from "./capabilityFilters";
import { navigate, repoPath, useCurrentRepo, useLocation } from "./router";
import { useApiResource } from "./useApiResource";

type MultiField = "plane" | "issueState" | "phase" | "roleCriticality";
const MULTI_FIELDS: readonly MultiField[] = ["plane", "issueState", "phase", "roleCriticality"];
const MULTI_FIELD_LABEL: Record<MultiField, string> = {
  plane: "plane",
  issueState: "status",
  phase: "phase",
  roleCriticality: "criticality",
};

interface Column {
  key: string;
  label: string;
  sortField?: SortField;
  optional?: boolean;
  value: (c: Capability) => string | string[];
}

// The always-visible columns plus a set the column chooser can toggle on.
// Local-only UI state, not URL-synced (unlike filters) — column visibility
// isn't part of what makes a link "shareable" in the spec §9 sense the same
// way a filtered/sorted/searched view is.
//
// Since the 2026-08-27 register→issues migration (see CLAUDE.md), Evidence
// and Notes are gone (volatile, frozen forever, never rendered as current)
// and Status is now the Live issue state, not the frozen status_category.
// Surface/Future surface are frozen-but-dated (rendered from snapshot, with
// its date shown once in the table's own caption rather than per cell).
const COLUMNS: Column[] = [
  { key: "id", label: "ID", sortField: "id", value: (c) => c.id },
  { key: "name", label: "Name", sortField: "name", value: (c) => c.name },
  { key: "plane", label: "Plane", sortField: "plane", value: (c) => c.plane },
  { key: "issueState", label: "Status", sortField: "issueState", value: (c) => c.issueState },
  { key: "phase", label: "Phase", sortField: "phase", value: (c) => c.snapshot.phase },
  { key: "roleCriticality", label: "Criticality", sortField: "roleCriticality", value: (c) => c.roleCriticality },
  { key: "adrReferences", label: "ADRs", value: (c) => c.adrReferences ?? [] },
  { key: "surface", label: "Surface", optional: true, value: (c) => c.snapshot.surface },
  { key: "futureSurface", label: "Future surface", optional: true, value: (c) => c.snapshot.futureSurface },
];
const DEFAULT_VISIBLE = new Set(COLUMNS.filter((c) => !c.optional).map((c) => c.key));

function csvEscape(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function toCSV(rows: Capability[], columns: Column[]): string {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const lines = rows.map((r) =>
    columns
      .map((c) => {
        const v = c.value(r);
        return csvEscape(Array.isArray(v) ? v.join("; ") : v);
      })
      .join(","),
  );
  return [header, ...lines].join("\n");
}

function downloadCSV(rows: Capability[], columns: Column[]) {
  const blob = new Blob([toCSV(rows, columns)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "capability-register.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// CapabilityRegister is the spec §9 register table: full-text search,
// multi-select filters, sortable columns, preset views, a column chooser,
// CSV export, and pagination — all client-side over the whole
// GET /api/capabilities list (see CLAUDE.md for why). Filter/sort/page
// state lives in the URL query string so the current view is always
// shareable and survives a reload.
export function CapabilityRegister({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<CapabilitiesResponse>(token, repo, "/capabilities");
  const caps = data?.capabilities ?? [];
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(DEFAULT_VISIBLE);
  const { search } = useLocation();
  const state = useMemo(() => parseFilterState(search.replace(/^\?/, "")), [search]);

  function updateState(patch: Partial<FilterState>, opts: { resetPage?: boolean } = { resetPage: true }) {
    const next: FilterState = { ...state, ...patch, page: opts.resetPage ? 1 : (patch.page ?? state.page) };
    navigate(`${repoPath(repo, "capabilities")}${serializeFilterState(next)}`, { replace: true });
  }

  function toggleMulti(field: MultiField, value: string) {
    const current = state[field];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    updateState({ [field]: next } as Partial<FilterState>);
  }

  function toggleSort(field: SortField) {
    if (state.sort === field) {
      updateState({ sort: field, dir: state.dir === "asc" ? "desc" : "asc" }, { resetPage: false });
    } else {
      updateState({ sort: field, dir: "asc" }, { resetPage: false });
    }
  }

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>Capability register</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>Capability register</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const filtered = applyFilters(caps, state);
  const pageRows = paginate(filtered, state.page);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const columns = COLUMNS.filter((c) => visibleColumns.has(c.key));
  const distinct = (field: MultiField) => Array.from(new Set(caps.map((c) => fieldValue(c, field)))).sort();
  const registerSync = data?.registerSync;
  const snapshotDate = caps[0]?.snapshot.date;

  return (
    <section className="panel register">
      <h2>Capability register</h2>
      {registerSync && (
        <p className={registerSync.stale ? "sync-note err" : "sync-note"}>
          {registerSync.fetchedAt ? `Register last fetched ${new Date(registerSync.fetchedAt).toLocaleString()}` : "Register not yet fetched"}
          {registerSync.stale && " — the latest refresh failed; showing the last known-good data."}
        </p>
      )}
      {snapshotDate && (
        <p className="muted-note">
          Status and criticality are live. Surface/Future surface are frozen as of {snapshotDate} — click through to a capability's issue
          for anything more current.
        </p>
      )}

      <div className="preset-views">
        {PRESET_VIEWS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn-ghost"
            title={p.description}
            onClick={() => updateState({ ...DEFAULT_FILTER_STATE, ...p.state })}
          >
            {p.label}
          </button>
        ))}
        <button type="button" className="btn-ghost" onClick={() => updateState(DEFAULT_FILTER_STATE)}>
          Clear filters
        </button>
      </div>

      <div className="register-toolbar">
        <input
          type="search"
          role="searchbox"
          aria-label="Search capabilities"
          placeholder="Search name, surface…"
          value={state.q}
          onChange={(e) => updateState({ q: e.target.value })}
        />

        {MULTI_FIELDS.map((field) => (
          <details key={field} className="filter-group">
            <summary>
              {MULTI_FIELD_LABEL[field]} {state[field].length > 0 ? `(${state[field].length})` : ""}
            </summary>
            <div className="filter-options">
              {distinct(field).map((value) => (
                <label key={value}>
                  <input type="checkbox" checked={state[field].includes(value)} onChange={() => toggleMulti(field, value)} />
                  {value}
                </label>
              ))}
            </div>
          </details>
        ))}

        <label className="filter-toggle">
          <input type="checkbox" checked={state.missingAdr} onChange={(e) => updateState({ missingAdr: e.target.checked })} />
          Missing ADR
        </label>

        <details className="filter-group">
          <summary>Columns</summary>
          <div className="filter-options">
            {COLUMNS.filter((c) => c.optional).map((c) => (
              <label key={c.key}>
                <input
                  type="checkbox"
                  checked={visibleColumns.has(c.key)}
                  onChange={() =>
                    setVisibleColumns((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.key)) next.delete(c.key);
                      else next.add(c.key);
                      return next;
                    })
                  }
                />
                {c.label}
              </label>
            ))}
          </div>
        </details>

        <button type="button" className="btn-ghost" onClick={() => downloadCSV(filtered, columns)}>
          Export CSV
        </button>
      </div>

      <p className="muted-note">
        {filtered.length} of {caps.length} capabilities
      </p>

      <div className="table-scroll">
        <table className="register-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>
                  {c.sortField ? (
                    <button type="button" onClick={() => toggleSort(c.sortField as SortField)}>
                      {c.label}
                      {state.sort === c.sortField ? (state.dir === "asc" ? " ▲" : " ▼") : ""}
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((c) => (
              <tr key={c.id} tabIndex={0} onClick={() => navigate(repoPath(repo, "capabilities", c.id))}>
                {columns.map((col) => {
                  const v = col.value(c);
                  return <td key={col.key}>{Array.isArray(v) ? v.join(", ") : v}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button type="button" disabled={state.page <= 1} onClick={() => updateState({ page: state.page - 1 }, { resetPage: false })}>
          ← Previous
        </button>
        <span>
          Page {state.page} of {totalPages}
        </span>
        <button
          type="button"
          disabled={state.page >= totalPages}
          onClick={() => updateState({ page: state.page + 1 }, { resetPage: false })}
        >
          Next →
        </button>
      </div>
    </section>
  );
}
